// VARIOS CARROS EN UNA PÓLIZA DE AUTO.
//
// Lissette, 2026-10-12: *"en la ficha de carros, tener la opción de escoger
// cuántos carros tendrá la póliza, y dependiendo de la cantidad de carros que
// coloque, las veces necesarias la información que tenemos solo para uno"*.
//
// Los carros viajan en `type_details.vehiculos` como una lista de
// { year, make, model, vin, plate }. No hace falta migración: `type_details` ya
// es un JSON libre y la clave entra por la lista blanca de camposDePoliza.js.
//
// EL PRIMER CARRO SE ESPEJA EN LOS CAMPOS DE SIEMPRE (vehicleYear, vehicleMake,
// vehicleModel, vin, licensePlate). Todo lo que ya los lee —búsqueda,
// historial de cambios, importar/exportar, variables de plantillas— sigue
// viendo el carro principal sin enterarse de que ahora puede haber más. Y una
// póliza vieja, sin `vehiculos`, se lee como un carro hecho de esos campos.
//
// Vive suelto, sin React: se prueba por lo que devuelve.

export const MAX_VEHICULOS = 10;

const CAMPOS = ['year', 'make', 'model', 'vin', 'plate'];
/** [campo del carro, campo plano de la póliza] para el carro principal. */
const ESPEJO = [['year', 'vehicleYear'], ['make', 'vehicleMake'], ['model', 'vehicleModel'], ['vin', 'vin'], ['plate', 'licensePlate']];

export const vehiculoVacio = () => ({ year: '', make: '', model: '', vin: '', plate: '' });
const limpio = (v) => String(v ?? '');
const normalizar = (v) => Object.fromEntries(CAMPOS.map((k) => [k, limpio(v?.[k])]));
export const tieneDatos = (v) => CAMPOS.some((k) => limpio(v?.[k]).trim());

/**
 * Los carros de una póliza, siempre al menos uno (vacío si no hay nada). Si la
 * póliza es de antes y no trae la lista, su único carro sale de los campos
 * de siempre.
 */
export function vehiculosDe(poliza) {
  const lista = Array.isArray(poliza?.vehiculos) ? poliza.vehiculos.slice(0, MAX_VEHICULOS).map(normalizar) : [];
  if (lista.length) {
    // El primero manda lo que diga el campo plano si alguien lo cambió por un
    // camino que no conoce la lista (un import, una edición en tabla).
    const primero = { ...lista[0] };
    for (const [k, plano] of ESPEJO) {
      const v = limpio(poliza?.[plano]);
      if (v.trim() && v !== primero[k]) primero[k] = v;
    }
    lista[0] = primero;
    return lista;
  }
  const deAntes = Object.fromEntries(ESPEJO.map(([k, plano]) => [k, limpio(poliza?.[plano])]));
  return [deAntes];
}

/** Cuántos carros tiene escritos (para enseñarlo en la tarjeta). */
export const cuantosVehiculos = (poliza) => vehiculosDe(poliza).filter(tieneDatos).length;

/**
 * La lista con N carros: agrega carros vacíos al final o quita los últimos.
 * `quitados` cuenta cuántos de los que se quitarían tienen datos, para que la
 * pantalla pregunte antes de borrarlos.
 */
export function conCantidad(lista, n) {
  const cantidad = Math.max(1, Math.min(MAX_VEHICULOS, Math.trunc(Number(n) || 1)));
  const base = (Array.isArray(lista) && lista.length ? lista : [vehiculoVacio()]).map(normalizar);
  if (base.length >= cantidad) {
    return { lista: base.slice(0, cantidad), quitados: base.slice(cantidad).filter(tieneDatos).length };
  }
  return { lista: [...base, ...Array.from({ length: cantidad - base.length }, vehiculoVacio)], quitados: 0 };
}

/**
 * Lo que se guarda en la póliza: la lista y el carro principal espejado en los
 * campos de siempre.
 */
export function camposConVehiculos(lista) {
  const l = (Array.isArray(lista) && lista.length ? lista : [vehiculoVacio()]).slice(0, MAX_VEHICULOS).map(normalizar);
  const fuera = { vehiculos: l };
  for (const [k, plano] of ESPEJO) fuera[plano] = l[0][k];
  return fuera;
}

/** "2021 Toyota Camry" — para la tarjeta y los resúmenes. */
export const nombreDeVehiculo = (v) => [v?.year, v?.make, v?.model].map((x) => limpio(x).trim()).filter(Boolean).join(' ');
